/**
 * Typed Sentry breadcrumb helpers for the Jantro Bazar client.
 *
 * Breadcrumbs are opt-in trails of user actions that Sentry attaches to any
 * error captured *afterwards*, so an issue timeline reads like a story:
 *   "searched 'brake pad' → added SKU-123 x2 to cart → clicked checkout →
 *    order submit failed"
 *
 * Rules:
 *   - No PII (name, email, phone, address, coordinates).
 *   - Keep payloads small (Sentry caps them, big objects are dropped).
 *   - Safe on the server (no-ops) — @sentry/react is client-only, so we
 *     dynamically resolve the SDK and swallow failures.
 */
import { withSentry } from "@/lib/sentry-lazy";

type Level = "info" | "warning" | "error";

function safe(fn: () => void) {
  try {
    fn();
  } catch {
    /* never let telemetry crash the app */
  }
}

function crumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: Level = "info",
) {
  safe(() => {
    withSentry((Sentry) =>
      Sentry.addBreadcrumb({ category, message, data, level, timestamp: Date.now() / 1000 }),
    );
  });
}

// ---------- Search ----------

export function breadcrumbSearchQuery(query: string, resultCount?: number) {
  crumb("search", `search: "${query.slice(0, 80)}"`, {
    query_length: query.length,
    result_count: resultCount ?? null,
  });
}

export function breadcrumbSearchSelected(kind: "product" | "brand" | "suggested", term: string) {
  crumb("search", `search select (${kind})`, { term: term.slice(0, 80) });
}

// ---------- Cart ----------

export function breadcrumbCartAdd(productId: string, qty: number) {
  crumb("cart", "cart: add", { product_id: productId, qty });
}

export function breadcrumbCartRemove(productId: string) {
  crumb("cart", "cart: remove", { product_id: productId });
}

export function breadcrumbCartUpdate(productId: string, qty: number) {
  crumb("cart", "cart: update qty", { product_id: productId, qty });
}

export function breadcrumbCartClear() {
  crumb("cart", "cart: clear");
}

// ---------- Checkout ----------

export function breadcrumbCheckoutEnter(itemCount: number) {
  crumb("checkout", "checkout: enter", { item_count: itemCount });
}

export function breadcrumbCheckoutSubmit(payment: string, itemCount: number) {
  crumb("checkout", "checkout: submit", { payment, item_count: itemCount });
}

export function breadcrumbCheckoutSuccess(orderNumber: string | null) {
  crumb("checkout", "checkout: success", { order_number: orderNumber ?? null });
}

export function breadcrumbCheckoutFailure(reason: string) {
  crumb("checkout", "checkout: failure", { reason: reason.slice(0, 200) }, "error");
}

// ---------- Supabase / API ----------

export function breadcrumbApiCall(
  op: string,
  outcome: "ok" | "error",
  data?: Record<string, unknown>,
) {
  crumb("api", `api: ${op} ${outcome}`, data, outcome === "error" ? "error" : "info");
}
